import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockWebinars } from '../data/webinarData';

const {
  FiArrowLeft, FiClock, FiVideo, FiShield, FiUsers, FiMessageSquare,
  FiMic, FiMicOff, FiCamera, FiCameraOff, FiCheck, FiSend, FiInfo, FiDollarSign,
} = FiIcons;

/**
 * Normalise a time string that may include AM/PM into a 24-hr "HH:MM" string
 * so that `new Date(\`${date}T${time}\`)` always produces a valid Date.
 */
const normaliseTime = (raw = '') => {
  const s = raw.trim();
  if (!s) return '00:00';
  // Already 24-hr  (e.g. "14:30")
  if (/^\d{1,2}:\d{2}$/.test(s)) return s;
  // 12-hr with AM/PM  (e.g. "2:30 PM")
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const min  = m[2];
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
};

/**
 * Parse a webinar's date + time strings into a numeric timestamp (ms).
 * Returns NaN if either field is missing or produces an Invalid Date.
 */
const parseWebinarMs = (date, time) => {
  if (!date || !time) return NaN;
  const normTime = normaliseTime(time);
  const ms = new Date(`${date}T${normTime}`).getTime();
  return isNaN(ms) ? NaN : ms;
};

/* ── Countdown unit display ── */
const CountdownUnit = ({ value, label }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
    <div className="text-3xl font-serif text-white mb-1 tabular-nums">
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-[8px] font-black uppercase text-white/30 tracking-widest">{label}</div>
  </div>
);

const WebinarLobby = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  /* Find webinar — undefined means unknown id */
  const webinar = mockWebinars.find(w => w.id === id);

  const [timeLeft, setTimeLeft]       = useState({ h: 0, m: 0, s: 0 });
  const [isReady, setIsReady]         = useState(false);
  const [isJoining, setIsJoining]     = useState(false);
  const [inGreenRoom, setInGreenRoom] = useState(false);
  const [micOn, setMicOn]             = useState(false);
  const [camOn, setCamOn]             = useState(false);
  const [messages, setMessages]       = useState([
    { id: 1, author: 'Host', text: "Welcome! We'll be starting soon.", time: '2m ago' },
  ]);
  const [newMsg, setNewMsg] = useState('');

  /**
   * Keep a stable ref to the interval so we can clear it from anywhere,
   * including the cleanup returned by the effect.
   */
  const intervalRef = useRef(null);
  const isMounted   = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    /* Unknown webinar — redirect immediately without starting a timer */
    if (!webinar) {
      navigate('/webinars', { replace: true });
      return;
    }

    /* ── Green room eligibility ── */
    if (webinar.greenRoom?.enabled) {
      const sessionMs = parseWebinarMs(webinar.date, webinar.time);
      if (!isNaN(sessionMs)) {
        const openMs = sessionMs - (webinar.greenRoom.openMinutesBefore ?? 30) * 60_000;
        const nowMs  = Date.now();
        if (nowMs >= openMs && nowMs < sessionMs) {
          if (isMounted.current) setInGreenRoom(true);
        }
      }
    }

    /* ── Countdown tick ── */
    const targetMs = parseWebinarMs(webinar.date, webinar.time);

    const tick = () => {
      if (!isMounted.current) return;

      if (isNaN(targetMs)) {
        /* Bad date/time — show zeros, keep ready=false, don't crash */
        setTimeLeft({ h: 0, m: 0, s: 0 });
        setIsReady(false);
        return;
      }

      const dist = targetMs - Date.now();
      if (dist <= 0) {
        setTimeLeft({ h: 0, m: 0, s: 0 });
        setIsReady(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        setTimeLeft({
          h: Math.floor(dist / 3_600_000),
          m: Math.floor((dist % 3_600_000) / 60_000),
          s: Math.floor((dist % 60_000) / 1_000),
        });
        setIsReady(false);
      }
    };

    tick();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(tick, 1_000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [webinar, navigate]);

  const handleJoin = useCallback(() => {
    if (!webinar) return;
    setIsJoining(true);
    setTimeout(() => {
      if (!isMounted.current) return;
      setIsJoining(false);
      window.open(`https://meet.jit.si/OneFinalLegacy-${webinar.id}`, '_blank');
    }, 1_500);
  }, [webinar]);

  const sendMsg = useCallback((e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setMessages(prev => [
      ...prev,
      { id: Date.now(), author: 'You', text: newMsg, time: 'now' },
    ]);
    setNewMsg('');
  }, [newMsg]);

  /* Render nothing while redirecting */
  if (!webinar) return null;

  const isFree = webinar.monetization?.isFree !== false;

  return (
    <div className="min-h-screen bg-accent-navy pt-24 pb-20 flex flex-col">
      <div className="container mx-auto px-6 flex-grow flex flex-col justify-center">
        <button
          onClick={() => navigate('/webinars')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-accent-gold mb-12 transition-colors w-max"
        >
          <SafeIcon icon={FiArrowLeft} /> Exit Lobby
        </button>

        <AnimatePresence mode="wait">
          {inGreenRoom ? (
            /* ── GREEN ROOM ── */
            <motion.div
              key="greenroom"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
            >
              <div className="space-y-8">
                <div>
                  <span className="bg-green-500 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 inline-block shadow-lg shadow-green-500/20">
                    🟢 Green Room Active
                  </span>
                  <h1 className="text-4xl md:text-5xl font-serif text-white mb-4 leading-tight">
                    {webinar.title}
                  </h1>
                  <p className="text-white/60 font-light leading-relaxed">
                    This is a private pre-session space. Check your audio and video before the session goes live.
                  </p>
                </div>

                <div className="flex items-center gap-4 text-white">
                  <SafeIcon icon={FiClock} className="text-accent-gold w-6 h-6" />
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/40">Session begins in</p>
                    <p className="text-2xl font-bold tabular-nums">
                      {String(timeLeft.h).padStart(2, '0')}:
                      {String(timeLeft.m).padStart(2, '0')}:
                      {String(timeLeft.s).padStart(2, '0')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => setMicOn(v => !v)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-bold transition-all ${
                      micOn
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/20'
                        : 'bg-white/10 text-white/60 border border-white/10 hover:bg-white/15'
                    }`}
                  >
                    <SafeIcon icon={micOn ? FiMic : FiMicOff} />
                    {micOn ? 'Mic On' : 'Mic Off'}
                  </button>
                  <button
                    onClick={() => setCamOn(v => !v)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-bold transition-all ${
                      camOn
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/20'
                        : 'bg-white/10 text-white/60 border border-white/10 hover:bg-white/15'
                    }`}
                  >
                    <SafeIcon icon={camOn ? FiCamera : FiCameraOff} />
                    {camOn ? 'Camera On' : 'Camera Off'}
                  </button>
                </div>

                {webinar.platformConfig?.platform === 'native' && (
                  <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-4 flex items-start gap-3">
                    <SafeIcon icon={FiInfo} className="text-red-400 w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-300 text-xs font-bold mb-1">RTMP Stream Active</p>
                      <p className="text-red-400/80 text-xs font-mono break-all">
                        {webinar.platformConfig.credentials?.rtmpUrl || 'rtmp://stream.onefinallegacy.com/live'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Green Room Chat */}
              <div
                className="bg-white/5 border border-white/10 rounded-[32px] p-6 flex flex-col"
                style={{ minHeight: '420px', maxHeight: '500px' }}
              >
                <div className="mb-4 flex-shrink-0">
                  <h3 className="text-lg font-serif text-white flex items-center gap-2">
                    <SafeIcon icon={FiMessageSquare} className="text-accent-gold" /> Green Room Chat
                  </h3>
                  <p className="text-[9px] font-black uppercase tracking-widest text-white/30 mt-1">
                    Private — Presenters only
                  </p>
                </div>

                <div className="flex-grow overflow-y-auto space-y-4 pr-1 min-h-0">
                  {messages.map(m => (
                    <div key={m.id} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-accent-gold/20 rounded-full flex items-center justify-center text-accent-gold text-xs font-bold flex-shrink-0">
                        {m.author[0]}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white text-sm">{m.author}</span>
                          <span className="text-[9px] text-white/30">{m.time}</span>
                        </div>
                        <p className="text-white/70 text-sm">{m.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={sendMsg} className="flex gap-2 mt-4 flex-shrink-0">
                  <input
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    placeholder="Message the team…"
                    className="flex-grow bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-accent-gold/40"
                  />
                  <button
                    type="submit"
                    className="px-5 py-3 bg-accent-gold text-white rounded-xl text-sm font-bold hover:bg-accent-gold/90 transition-colors"
                  >
                    <SafeIcon icon={FiSend} />
                  </button>
                </form>
              </div>
            </motion.div>
          ) : (
            /* ── REGULAR LOBBY ── */
            <motion.div
              key="lobby"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            >
              <div className="space-y-10">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <span className="bg-accent-gold text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 inline-block shadow-lg shadow-accent-gold/20">
                    Secure Waiting Room
                  </span>
                  <h1 className="text-4xl md:text-6xl font-serif text-white mb-6 leading-tight">
                    {webinar.title}
                  </h1>
                  <p className="text-white/60 text-lg font-light leading-relaxed max-w-xl">
                    Preparing the sanctuary for your session. Please ensure your audio is connected
                    and you are in a peaceful space.
                  </p>
                </motion.div>

                <div className="grid grid-cols-3 gap-6 max-w-md">
                  <CountdownUnit value={timeLeft.h} label="Hours"   />
                  <CountdownUnit value={timeLeft.m} label="Minutes" />
                  <CountdownUnit value={timeLeft.s} label="Seconds" />
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="bg-white/5 border border-white/10 px-6 py-4 rounded-[24px] flex items-center gap-4">
                    <SafeIcon icon={FiShield} className="text-accent-gold w-6 h-6" />
                    <div>
                      <p className="text-[9px] font-black uppercase text-white/40 tracking-widest">Status</p>
                      <p className="text-xs font-bold text-white uppercase">End-to-End Encrypted</p>
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 px-6 py-4 rounded-[24px] flex items-center gap-4">
                    <SafeIcon icon={FiUsers} className="text-accent-gold w-6 h-6" />
                    <div>
                      <p className="text-[9px] font-black uppercase text-white/40 tracking-widest">Waiting</p>
                      <p className="text-xs font-bold text-white uppercase">
                        {webinar.registrants ?? 0} Registered
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-[40px] p-10 md:p-14 border border-white/10 shadow-2xl relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/5 rounded-full -mr-32 -mt-32 blur-3xl opacity-50 pointer-events-none" />
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif text-accent-navy mb-8 flex items-center gap-3">
                    <SafeIcon icon={FiVideo} className="text-accent-gold" /> Session Facilitator
                  </h3>

                  <div className="flex items-start gap-6 mb-10">
                    <img
                      src="https://i.pravatar.cc/150?u=fac"
                      alt=""
                      className="w-20 h-20 rounded-3xl object-cover border-4 border-brand-light shadow-xl"
                    />
                    <div>
                      <h4 className="text-xl font-bold text-accent-navy mb-2">
                        {webinar.speaker || 'Facilitator'}
                      </h4>
                      <p className="text-text-muted font-light leading-relaxed text-sm">
                        {webinar.speakerBio || 'Details will be shared before the session.'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-brand-light/50 p-6 rounded-3xl border border-brand-dark mb-8">
                    <h5 className="text-[10px] font-black uppercase text-accent-navy tracking-widest mb-3 flex items-center gap-2">
                      <SafeIcon icon={FiMessageSquare} /> Pre-Session Notes
                    </h5>
                    <p className="text-sm text-text-muted font-light leading-relaxed italic">
                      "Please have a journal ready. We'll cover practical steps and emotional aspects of legacy building."
                    </p>
                  </div>

                  {!isFree && (
                    <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
                      <SafeIcon icon={FiDollarSign} className="text-amber-500 w-4 h-4 shrink-0 mt-0.5" />
                      <div className="text-amber-700 text-xs">
                        <p className="font-bold mb-1">Payment Confirmed</p>
                        <p>
                          Your {webinar.monetization?.currency ?? 'USD'}{' '}
                          {webinar.monetization?.price ?? 0} registration is confirmed.
                        </p>
                        {webinar.monetization?.hasReplay && (
                          <p className="flex items-center gap-1 mt-1 text-green-600 font-bold">
                            <SafeIcon icon={FiCheck} className="w-3 h-3" /> Replay access included
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    disabled={!isReady || isJoining}
                    onClick={handleJoin}
                    className={`w-full py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-sm transition-all flex items-center justify-center gap-3 ${
                      isReady
                        ? 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-xl shadow-accent-gold/20 cursor-pointer'
                        : 'bg-brand-dark text-text-muted opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {isJoining ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : isReady ? (
                      <>Enter Sanctuary Session <SafeIcon icon={FiVideo} /></>
                    ) : (
                      <>Session not started yet — waiting…</>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WebinarLobby;